import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Printer, RotateCcw, Search } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { inputClass, checkboxClass } from '../components/ui/Input';
import { SkeletonList } from '../components/ui/Skeleton';
import { RecordPaymentDialog } from '../components/collection/RecordPaymentDialog';
import { PrintDocumentDialog } from '../components/PrintDocumentDialog';
import { useAction } from '../auth/usePermission';
import { formatDateTime, formatDate, formatEUR } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';

/**
 * Screen — unified document list. One screen over quotes, orders, delivery notes, invoices,
 * recaps, credit notes, returns and pickup slips (GET /documents), because a counter handling
 * hundreds of documents a day works by "find the paperwork for this customer / this number /
 * this week", not by remembering which of eight screens a document happens to live on.
 *
 * All filtering, sorting and paging is server-side: the list is unbounded, so filtering a
 * client-side page would only ever filter the twenty rows that happened to be fetched.
 */

type DocType =
  | 'QUOTE'
  | 'ORDER'
  | 'DELIVERY_NOTE'
  | 'INVOICE'
  | 'RECAP'
  | 'CREDIT_NOTE'
  | 'RETURN'
  | 'PICKUP_SLIP';

interface DocumentRow {
  id: string;
  docType: DocType;
  number: string;
  date: string;
  status: string | null;
  customerId: string | null;
  customerCode: string | null;
  customerName: string | null;
  customerType: string | null;
  totalHT: number;
  totalVAT: number;
  totalTTC: number;
  balanceDue: number | null;
  paymentStatus: string | null;
  paymentMethod: string | null;
  paymentTerms: string | null;
  dueDate: string | null;
  createdBy: string | null;
  licensePlate: string | null;
  orderId: string | null;
}

interface Facets {
  types: DocType[];
  statusesByType: Record<string, string[]>;
  customerTypes: string[];
  paymentMethods: string[];
  paymentTerms: string[];
  users: string[];
}

/** Short tag shown in the type column — the client reads documents by their prefix. */
const TYPE_TAG: Record<DocType, string> = {
  QUOTE: 'DEV',
  ORDER: 'CMD',
  DELIVERY_NOTE: 'BL',
  INVOICE: 'FAC',
  RECAP: 'RECAP',
  CREDIT_NOTE: 'AVO',
  RETURN: 'RET',
  PICKUP_SLIP: 'BR',
};

const TYPE_TONE: Record<DocType, string> = {
  QUOTE: 'bg-muted text-foreground',
  ORDER: 'bg-blue-100 text-blue-800',
  DELIVERY_NOTE: 'bg-indigo-100 text-indigo-800',
  INVOICE: 'bg-success/20 text-success',
  RECAP: 'bg-success/10 text-success',
  CREDIT_NOTE: 'bg-destructive/10 text-destructive',
  RETURN: 'bg-warning/20 text-warning',
  PICKUP_SLIP: 'bg-muted text-foreground',
};

/** Families with a real renderer; the rest of the list cannot be printed. A BL prints through
 *  the DELIVERY_NOTE type, whose two variants (driver / priced client copy) are the template
 *  selector inside the dialog. */
const PRINTABLE: Partial<Record<DocType, 'INVOICE' | 'QUOTE' | 'CREDIT_NOTE' | 'DELIVERY_NOTE'>> = {
  INVOICE: 'INVOICE',
  RECAP: 'INVOICE',
  QUOTE: 'QUOTE',
  CREDIT_NOTE: 'CREDIT_NOTE',
  DELIVERY_NOTE: 'DELIVERY_NOTE',
};

const PAGE_SIZE = 25;

interface FilterState {
  type: string;
  status: string;
  customer: string;
  customerType: string;
  paymentMethod: string;
  paymentTerms: string;
  createdBy: string;
  dateFrom: string;
  dateTo: string;
  dueFrom: string;
  dueTo: string;
  unpaidOnly: boolean;
  licensePlate: string;
  q: string;
}

const EMPTY: FilterState = {
  type: '',
  status: '',
  customer: '',
  customerType: '',
  paymentMethod: '',
  paymentTerms: '',
  createdBy: '',
  dateFrom: '',
  dateTo: '',
  dueFrom: '',
  dueTo: '',
  unpaidOnly: false,
  licensePlate: '',
  q: '',
};

/**
 * `fixedType` pins the list to one family and hides the type selector — used by the Quotes
 * screen so it gets the same search, filters, columns and readable dates as the main document
 * list from ONE implementation. Two copies of a filter bar drift; this cannot.
 */
export default function Documents({
  fixedType,
  titleKey,
  descriptionKey,
}: {
  fixedType?: DocType;
  /** i18n keys under `documents.*`, so the Quotes screen re-titles this list without copy here. */
  titleKey?: string;
  descriptionKey?: string;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError } = useToast();
  // `?customer=` — the order sheet's "all the customer's documents" link lands pre-filtered.
  const [params] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() => {
    const base = fixedType ? { ...EMPTY, type: fixedType } : EMPTY;
    const customer = params.get('customer');
    return customer ? { ...base, customer } : base;
  });
  const [rows, setRows] = useState<DocumentRow[]>([]);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('date,desc');
  const [loading, setLoading] = useState(true);
  // INVOICE_PAY is the effective authority (role matrix ± per-account overrides) and is the same
  // action the server guard resolves, so the control cannot drift from what the API will allow.
  // In practice it resolves to PAYMENT_RECONCILE = ACCOUNTING + OWNER.
  const canSettle = useAction('INVOICE_PAY');
  const [selected, setSelected] = useState<Record<string, DocumentRow>>({});
  const [settling, setSettling] = useState(false);
  const [printing, setPrinting] = useState<DocumentRow | null>(null);

  const set = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0); // a changed filter invalidates the page cursor — page 3 of the old result set is meaningless
  };

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.type) p.set('types', filters.type);
    if (filters.status) p.set('status', filters.status);
    if (filters.customer) p.set('customer', filters.customer);
    if (filters.customerType) p.set('customerType', filters.customerType);
    if (filters.paymentMethod) p.set('paymentMethod', filters.paymentMethod);
    if (filters.paymentTerms) p.set('paymentTerms', filters.paymentTerms);
    if (filters.createdBy) p.set('createdBy', filters.createdBy);
    if (filters.dateFrom) p.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) p.set('dateTo', filters.dateTo);
    if (filters.dueFrom) p.set('dueFrom', filters.dueFrom);
    if (filters.dueTo) p.set('dueTo', filters.dueTo);
    if (filters.unpaidOnly) p.set('unpaidOnly', 'true');
    if (filters.licensePlate.trim()) p.set('licensePlate', filters.licensePlate.trim());
    if (filters.q.trim()) p.set('q', filters.q.trim());
    p.set('page', String(page));
    p.set('size', String(PAGE_SIZE));
    p.set('sort', sort);
    return p.toString();
  }, [filters, page, sort]);

  // Debounced so typing in the free-search box doesn't fire a query per keystroke against a
  // UNION over eight tables.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ content: DocumentRow[]; totalElements: number }>(
        `/documents?${queryString}`
      );
      setRows(data.content ?? []);
      setTotal(data.totalElements ?? 0);
    } catch (err) {
      showApiError(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryString, showApiError]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void load(), 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [load]);

  useEffect(() => {
    apiClient
      .get<{ data: Facets }>('/documents/facets')
      .then((r) => setFacets(r.data.data))
      .catch(() => setFacets(null)); // filter bar degrades to free-text only
  }, []);

  // Statuses depend on the selected type: showing all 25 statuses of eight families at once
  // makes the dropdown useless, and most combinations return nothing.
  const statusOptions = useMemo(() => {
    if (!facets) return [];
    const src = filters.type
      ? (facets.statusesByType[filters.type] ?? [])
      : Object.values(facets.statusesByType).flat();
    return [...new Set(src)].sort();
  }, [facets, filters.type]);

  // Only an invoice or recap still carrying a balance can be settled. A quote or BL is not owed,
  // and a paid invoice has nothing left to allocate to.
  const settleable = (r: DocumentRow) =>
    canSettle &&
    (r.docType === 'INVOICE' || r.docType === 'RECAP') &&
    r.customerId != null &&
    (r.balanceDue ?? 0) > 0;

  const chosen = Object.values(selected);
  // Allocation runs per CUSTOMER, so a selection spanning two customers has no meaning. Picking
  // a different customer's invoice starts a fresh selection rather than silently mixing them.
  const selectedCustomerId = chosen[0]?.customerId ?? null;
  const selectedTotal = chosen.reduce((s, r) => s + (r.balanceDue ?? 0), 0);

  const toggleRow = (r: DocumentRow) => {
    setSelected((prev) => {
      const key = `${r.docType}-${r.id}`;
      if (prev[key]) {
        const rest = { ...prev };
        delete rest[key];
        return rest;
      }
      const differentCustomer =
        Object.values(prev)[0] && Object.values(prev)[0].customerId !== r.customerId;
      return differentCustomer ? { [key]: r } : { ...prev, [key]: r };
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeCount = Object.entries(filters).filter(
    ([k, v]) => v !== '' && v !== false && k !== 'q'
  ).length;

  const openRow = (r: DocumentRow) => {
    // Every family has a detail screen except the ones that only exist against an order, which
    // open the order they belong to.
    switch (r.docType) {
      case 'ORDER':
        return navigate(`/orders/${r.id}`);
      case 'QUOTE':
        return navigate(`/quotes/${r.id}`);
      // The list pages own the detail panel (summary + PDF preview); `?open=` lands on it with
      // THIS document already open instead of on a list the operator then has to search again.
      case 'INVOICE':
        return navigate(`/invoices?open=${r.id}`);
      case 'RECAP':
        return navigate(`/recap-invoices?open=${r.id}`);
      case 'CREDIT_NOTE':
        return navigate(`/credit-notes?open=${r.id}`);
      case 'RETURN':
        return navigate(`/returns`);
      default:
        return r.orderId ? navigate(`/orders/${r.orderId}`) : undefined;
    }
  };

  const sortBy = (col: string) => {
    const [cur, dir] = sort.split(',');
    setSort(cur === col && dir === 'desc' ? `${col},asc` : `${col},desc`);
    setPage(0);
  };

  return (
    <div>
      <PageHeader
        title={t(titleKey ?? 'documents.title')}
        description={t(descriptionKey ?? 'documents.description')}
        /* One way in, not two. "New Order" used to sit beside "New Sale" on this header — the
           same screen backs /orders, /quotes and /documents, so the counter met the choice on
           every list. Both buttons opened OrderCreate, which has been ONE flow since 2026-09-18
           (the route decides pay-now vs deferred), so the second button asked the seller to
           answer a question the sale itself answers later. /orders/new is still routed and is
           still where a quote converts — it just is not offered as an alternative entry point. */
        actions={
          <Button size="sm" onClick={() => navigate('/pos/sale')}>
            {t('orders.list.newSale')}
          </Button>
        }
      />

      {/* Filter bar */}
      <div className="mb-3 rounded-lg border bg-card p-3 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              className={`${inputClass} pl-8`}
              placeholder={t('documents.searchPlaceholder')}
              value={filters.q}
              onChange={(e) => set('q', e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFilters(fixedType ? { ...EMPTY, type: fixedType } : EMPTY);
              setPage(0);
            }}
            disabled={activeCount === 0 && !filters.q}
          >
            <RotateCcw className="h-4 w-4" /> {t('common.reset')}
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
          {!fixedType && (
            <Field label={t('documents.filters.type')}>
              <Select value={filters.type} onChange={(e) => set('type', e.target.value)}>
                <option value="">{t('documents.filters.allTypes')}</option>
                {(facets?.types ?? []).map((dt) => (
                  <option key={dt} value={dt}>
                    {t(`documents.types.${dt}`, { defaultValue: dt })}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label={t('documents.filters.state')}>
            <Select value={filters.status} onChange={(e) => set('status', e.target.value)}>
              <option value="">{t('documents.filters.allStates')}</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {enumLabel('docStatus', s)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('documents.filters.customer')}>
            <input
              className={inputClass}
              placeholder={t('documents.filters.customerPlaceholder')}
              value={filters.customer}
              onChange={(e) => set('customer', e.target.value)}
            />
          </Field>

          <Field label={t('documents.filters.plate')}>
            <input
              className={`${inputClass} uppercase`}
              placeholder="AB123CD"
              value={filters.licensePlate}
              // Uppercased as you type so what is on screen matches what is stored and searched;
              // the server normalises again, so punctuation typed off a windscreen still matches.
              onChange={(e) => set('licensePlate', e.target.value.toUpperCase())}
            />
          </Field>

          <Field label={t('documents.filters.customerType')}>
            <Select
              value={filters.customerType}
              onChange={(e) => set('customerType', e.target.value)}
            >
              <option value="">{t('common.all')}</option>
              {(facets?.customerTypes ?? []).map((ct) => (
                <option key={ct} value={ct}>
                  {enumLabel('customerType', ct)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('documents.filters.paymentMethod')}>
            <Select
              value={filters.paymentMethod}
              onChange={(e) => set('paymentMethod', e.target.value)}
            >
              <option value="">{t('common.all')}</option>
              {(facets?.paymentMethods ?? []).map((m) => (
                <option key={m} value={m}>
                  {enumLabel('paymentMethod', m)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('documents.filters.paymentTerms')}>
            <Select
              value={filters.paymentTerms}
              onChange={(e) => set('paymentTerms', e.target.value)}
            >
              <option value="">{t('common.all')}</option>
              {(facets?.paymentTerms ?? []).map((pt) => (
                <option key={pt} value={pt}>
                  {enumLabel('paymentTerms', pt)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('documents.filters.createdBy')}>
            <Select value={filters.createdBy} onChange={(e) => set('createdBy', e.target.value)}>
              <option value="">{t('documents.filters.anyone')}</option>
              {(facets?.users ?? []).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('documents.filters.periodFrom')}>
            <input
              type="date"
              className={inputClass}
              value={filters.dateFrom}
              onChange={(e) => set('dateFrom', e.target.value)}
            />
          </Field>
          <Field label={t('documents.filters.periodTo')}>
            <input
              type="date"
              className={inputClass}
              value={filters.dateTo}
              onChange={(e) => set('dateTo', e.target.value)}
            />
          </Field>
          <Field label={t('documents.filters.dueFrom')}>
            <input
              type="date"
              className={inputClass}
              value={filters.dueFrom}
              onChange={(e) => set('dueFrom', e.target.value)}
            />
          </Field>
          <Field label={t('documents.filters.dueTo')}>
            <input
              type="date"
              className={inputClass}
              value={filters.dueTo}
              onChange={(e) => set('dueTo', e.target.value)}
            />
          </Field>

          <Field label={t('documents.filters.balance')}>
            <label className="flex h-9 items-center gap-2 text-sm">
              <input
                type="checkbox"
                className={checkboxClass}
                checked={filters.unpaidOnly}
                onChange={(e) => set('unpaidOnly', e.target.checked)}
              />
              {t('documents.filters.outstandingOnly')}
            </label>
          </Field>
        </div>
      </div>

      {/* Result table */}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b px-4 py-2 text-sm">
          <span className="text-muted-foreground">
            {loading ? t('common.loading') : t('documents.count', { count: total })}
            {activeCount > 0 &&
              !loading &&
              ` · ${t('documents.filterCount', { count: activeCount })}`}
          </span>
          <Pager page={page} totalPages={totalPages} loading={loading} onPage={setPage} />
        </div>

        {chosen.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-primary/5 px-4 py-2 text-sm">
            <span>
              {t('documents.selection', {
                count: chosen.length,
                amount: formatEUR(selectedTotal),
              })}
              <span className="ml-1 text-xs text-muted-foreground">
                ({chosen[0].customerName ?? t('documents.fallbackCustomer')})
              </span>
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelected({})}>
                {t('documents.clear')}
              </Button>
              <Button size="sm" onClick={() => setSettling(true)}>
                {t('documents.recordPayment')}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="p-4">
            <SkeletonList rows={8} />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            {t('documents.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  {canSettle && <Th />}
                  {!fixedType && <Th>{t('documents.columns.type')}</Th>}
                  <Th sortKey="number" sort={sort} onSort={sortBy}>
                    {t('documents.columns.number')}
                  </Th>
                  <Th sortKey="date" sort={sort} onSort={sortBy}>
                    {t('documents.columns.date')}
                  </Th>
                  <Th sortKey="customer_name" sort={sort} onSort={sortBy}>
                    {t('documents.columns.customer')}
                  </Th>
                  <Th>{t('documents.columns.plate')}</Th>
                  <Th sortKey="status" sort={sort} onSort={sortBy}>
                    {t('documents.columns.state')}
                  </Th>
                  <Th align="right">{t('documents.columns.ht')}</Th>
                  <Th align="right">{t('documents.columns.vat')}</Th>
                  <Th align="right" sortKey="total_ttc" sort={sort} onSort={sortBy}>
                    {t('documents.columns.ttc')}
                  </Th>
                  <Th align="right">{t('documents.columns.balanceDue')}</Th>
                  <Th>{t('documents.columns.payment')}</Th>
                  <Th sortKey="due_date" sort={sort} onSort={sortBy}>
                    {t('documents.columns.dueDate')}
                  </Th>
                  <Th>{t('documents.columns.createdBy')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.docType}-${r.id}`}
                    onClick={() => openRow(r)}
                    className={`cursor-pointer border-b last:border-b-0 hover:bg-muted/50 ${
                      selected[`${r.docType}-${r.id}`] ? 'bg-primary/5' : ''
                    }`}
                  >
                    {canSettle && (
                      // stopPropagation: the row opens the document, the checkbox selects it.
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        {settleable(r) ? (
                          <input
                            type="checkbox"
                            className={checkboxClass}
                            aria-label={t('documents.selectRow', { number: r.number })}
                            checked={!!selected[`${r.docType}-${r.id}`]}
                            onChange={() => toggleRow(r)}
                          />
                        ) : null}
                      </td>
                    )}
                    {!fixedType && (
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${TYPE_TONE[r.docType]}`}
                        >
                          {TYPE_TAG[r.docType]}
                        </span>
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 font-medium">{r.number}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {formatDateTime(r.date)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="max-w-[16rem] truncate">{r.customerName ?? '—'}</div>
                      {r.customerCode && (
                        <div className="text-[11px] text-muted-foreground">
                          {r.customerCode}
                          {r.customerType && ` · ${enumLabel('customerType', r.customerType)}`}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                      {r.licensePlate ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {r.status && (
                        <span className="rounded bg-muted px-2 py-0.5 text-xs">
                          {enumLabel('docStatus', r.status)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {formatEUR(r.totalHT)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatEUR(r.totalVAT)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums">
                      {formatEUR(r.totalTTC)}
                    </td>
                    {/* A quote or delivery note is not owed, so it shows nothing rather than 0,00 €. */}
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {r.balanceDue == null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={
                            r.balanceDue > 0 ? 'text-destructive' : 'text-muted-foreground'
                          }
                        >
                          {formatEUR(r.balanceDue)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs">
                      {r.paymentMethod ? (
                        enumLabel('paymentMethod', r.paymentMethod)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {r.paymentTerms && (
                        <div className="text-[11px] text-muted-foreground">
                          {enumLabel('paymentTerms', r.paymentTerms)}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {r.dueDate ? formatDate(r.dueDate) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {r.createdBy ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {PRINTABLE[r.docType] && (
                        <button
                          onClick={() => setPrinting(r)}
                          className="inline-flex items-center rounded border p-1 hover:bg-accent"
                          aria-label={t('documents.printRow', { number: r.number })}
                          title={t('documents.printTitle')}
                        >
                          <Printer className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Repeated below the rows so a long page doesn't have to be scrolled back up to
            turn it. Hidden on the empty state, where there is nothing to page through. */}
        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between border-t px-4 py-2 text-sm">
            <span className="text-xs text-muted-foreground">
              {t('documents.range', {
                from: page * PAGE_SIZE + 1,
                to: page * PAGE_SIZE + rows.length,
                total,
              })}
            </span>
            <Pager page={page} totalPages={totalPages} loading={loading} onPage={setPage} />
          </div>
        )}
      </div>

      {printing && PRINTABLE[printing.docType] && (
        <PrintDocumentDialog
          docType={PRINTABLE[printing.docType]!}
          documentId={printing.id}
          documentNumber={printing.number}
          customerId={printing.customerId}
          orderId={printing.orderId}
          onClose={() => setPrinting(null)}
        />
      )}

      {settling && selectedCustomerId && (
        // Migration 151: the one payment dialog (a règlement, visible in Encaissements). The ticked
        // invoices are pre-filled as the payer's designation; the operator can change every line.
        <RecordPaymentDialog
          customerId={selectedCustomerId}
          invoiceIds={chosen
            .filter((r) => r.docType === 'INVOICE' || r.docType === 'RECAP')
            .map((r) => r.id)}
          onClose={() => setSettling(false)}
          onDone={() => {
            setSelected({});
            void load();
          }}
        />
      )}
    </div>
  );
}

function Pager({
  page,
  totalPages,
  loading,
  onPage,
}: {
  page: number;
  totalPages: number;
  loading: boolean;
  onPage: (updater: (p: number) => number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        variant="outline"
        aria-label={t('documents.prevPage')}
        disabled={page === 0 || loading}
        onClick={() => onPage((p) => Math.max(0, p - 1))}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="px-2 text-xs text-muted-foreground">
        {page + 1} / {totalPages}
      </span>
      <Button
        size="sm"
        variant="outline"
        aria-label={t('documents.nextPage')}
        disabled={page + 1 >= totalPages || loading}
        onClick={() => onPage((p) => p + 1)}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function Th({
  children,
  align = 'left',
  sortKey,
  sort,
  onSort,
}: {
  /** Optional: the selection column has a deliberately empty header. */
  children?: React.ReactNode;
  align?: 'left' | 'right';
  sortKey?: string;
  sort?: string;
  onSort?: (col: string) => void;
}) {
  const [cur, dir] = (sort ?? '').split(',');
  const active = sortKey && cur === sortKey;
  return (
    <th
      className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'} ${
        sortKey ? 'cursor-pointer select-none hover:text-foreground' : ''
      }`}
      onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}
    >
      {children}
      {active && <span className="ml-1">{dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}
